import { hubCall } from "@/lib/api/hub";
/**
 * LLM Provider Service — calls hub proxy for /llm/providers endpoints.
 * All methods mirror the shape of connector.service.ts.
 */
import type { LLMProvider, CreateProviderInput, UpdateProviderInput, VerifyOutcome, SyncOutcome } from "@/types/llm-provider";
import type { ProbeModelResult } from "@/types/llm-model";
import type { ApiResponse, PaginatedResponse } from "@/types/api.types";

export type ProbeModelRequest = {
  modelId: string;
  /** Restricted by BE binding to {chat, embed}; sent verbatim. */
  capabilitiesToTest: string[];
};

export const llmProviderService = {
  async list(): Promise<PaginatedResponse<LLMProvider>> {
    return hubCall<PaginatedResponse<LLMProvider>>("/llm/providers");
  },

  async getById(id: string): Promise<ApiResponse<LLMProvider>> {
    return hubCall<ApiResponse<LLMProvider>>(`/llm/providers/${id}`);
  },

  async create(payload: CreateProviderInput): Promise<ApiResponse<LLMProvider>> {
    return hubCall<ApiResponse<LLMProvider>>("/llm/providers", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  },

  async update(id: string, payload: UpdateProviderInput): Promise<ApiResponse<LLMProvider>> {
    return hubCall<ApiResponse<LLMProvider>>(`/llm/providers/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
  },

  async delete(id: string): Promise<ApiResponse<{ deleted: boolean }>> {
    return hubCall<ApiResponse<{ deleted: boolean }>>(`/llm/providers/${id}`, {
      method: "DELETE",
    });
  },

  /**
   * Trigger a connectivity probe against the provider. signal lets the
   * caller (typically the VerifyButton with a 12s AbortController) cancel
   * the request — BE still owns truth and persists the outcome regardless,
   * so reload after a client-side abort surfaces the real result.
   */
  async verify(id: string, signal?: AbortSignal): Promise<ApiResponse<VerifyOutcome>> {
    return hubCall<ApiResponse<VerifyOutcome>>(`/llm/providers/${id}/verify`, {
      method: "POST",
      signal,
    });
  },

  /**
   * Probe a custom model_id against the verified provider. BE caps the call
   * at 120s server-side; pass a matching AbortSignal so the caller (modal
   * close) can drop the in-flight request locally without waiting for the
   * BE to time out.
   */
  async probeModel(
    id: string,
    body: ProbeModelRequest,
    signal?: AbortSignal,
  ): Promise<ApiResponse<ProbeModelResult>> {
    return hubCall<ApiResponse<ProbeModelResult>>(`/llm/providers/${id}/probe-model`, {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    });
  },

  /**
   * Manually re-fetch and reconcile the provider's model catalog.
   * BE rate-limits to 1/min/provider org-wide and may take ~30s for a
   * slow upstream listing — caller passes an AbortSignal to drop the
   * in-flight request locally on unmount/cancel.
   */
  async syncModels(id: string, signal?: AbortSignal): Promise<ApiResponse<SyncOutcome>> {
    return hubCall<ApiResponse<SyncOutcome>>(`/llm/providers/${id}/sync-models`, {
      method: "POST",
      signal,
    });
  },
};
